"use client";

import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";

import { cn } from "@/lib/utils";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";

const tabVariants = cva("max-h-full h-[var(--control-height)] group", {
  variants: {
    variant: {
      default:
        "bg-none items-center border-0 flex justify-center  border-b-2 border-b-gray-300",
      button:
        "bg-gray-100 items-center border border-primary justify-center rounded-lg flex p-[3px] space-x-2",
    },
    size: {
      default: "h-11 px-4 py-2",
      xs: "h-7 text-xs rounded-md gap-1.5 px-2.5",
      sm: "h-9 rounded-md gap-1.5 px-3",
      lg: "h-13 rounded-md px-6",
      xl: "h-15 rounded-md px-6",
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
});

function Tabs({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn("flex flex-col gap-2", className)}
      {...props}
    />
  );
}

function TabsList({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> &
  VariantProps<typeof tabVariants>) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      data-type={variant}
      className={cn(tabVariants({ variant, className }), `tab-${variant}`)}
      {...props}
    />
  );
}

function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger> &
  VariantProps<typeof tabVariants>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "font-semibold border-0 text-secondary-foreground/80 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring dark:data-[state=active]:border-input dark:data-[state=active]:bg-input/30 dark:text-muted-foreground inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-md p-2 whitespace-nowrap transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        "data-[state=active]:bg-primary data-[state=inactive]:bg-none data-[state=active]:text-black data-[state=active]:border-0 dark:data-[state=active]:text-foreground ",
        "group-data-[type=default]:bg-transparent group-data-[type=default]:border-transparent group-data-[type=default]:text-foreground group-data-[type=default]:mb-[-4px] group-data-[type=default]:rounded-none group-data-[type=default]:bg-none group-data-[type=default]:data-[state=active]:text-black group-data-[type=default]:data-[state=active]:border-b-4 group-data-[type=default]:data-[state=active]:border-b-primary  group-data-[type=default]:data-[state=active]:bg-transparent  group-data-[type=default]:data-[state=active]:border-primary group-data-[type=default]:border-b-4",
        className,
      )}
      {...props}
    />
  );
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("flex-1 outline-none", className)}
      {...props}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
